"use client";

import { clientsApi } from "#/api/clientsApi";
import { ClientContext } from "#/context/ClientContext";
import type { Client } from "#/types/client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";

function getSubdomain(): string | null {
  if (typeof window === "undefined") return null;
  const parts = window.location.hostname.split(".");
  if (parts.length < 3) return null;
  return parts[0];
}

export const ClientProvider = ({ children }: { children: ReactNode }) => {
  const [client, setClientState] = useState<Client | null>(null);
  const [subdomain, setSubdomain] = useState<string | null>(null);

  const { data, isSuccess } = useQuery({
    enabled: !!subdomain && !client,
    queryKey: ["client", subdomain],
    queryFn: () => clientsApi.getBySlug(subdomain!),
    retry: 1,
  });

  function processClient(newClient: Client) {
    setClientState(newClient);

    try {
      if (newClient?.color) {
        localStorage.setItem("client-color", newClient.color);
        document.documentElement.style.setProperty(
          "--client-color",
          newClient.color
        );
      }
    } catch (error) {
      console.error("[ClientProvider] Error saving to localStorage:", error);
    }
  }

  useEffect(() => {
    const sd = getSubdomain();
    setSubdomain(sd);
  }, []);

  useEffect(() => {
    if (isSuccess && data?.data) {
      processClient(data.data);
    } else {
      const clientcolor = localStorage.getItem("client-color");
      if (clientcolor) {
        document.documentElement.style.setProperty("--client-color", clientcolor);
      }
    }
  }, [isSuccess, data]);

  return (
    <ClientContext.Provider value={{ client, setClient: processClient }}>
      {children}
    </ClientContext.Provider>
  );
};
